import React, { useMemo } from "react";
import { AppointmentConfiguration } from "../../../Redux/digital-product/types";
import { format } from "date-fns";

interface TimeSlotsProps {
  selectedSlot: string;
  onSlotSelect: (slot: string) => void;
  config?: AppointmentConfiguration;
  selectedDate: Date;
}

const TimeSlots: React.FC<TimeSlotsProps> = ({ selectedSlot, onSlotSelect, config, selectedDate }) => {
  
  const generatedSlots = useMemo(() => {
    if (!config) return [];

    const dayName = format(selectedDate, "eeee").toLowerCase();
    const daySchedule = (config.availabilitySchedule.weeklySchedule as any)[dayName];

    if (!daySchedule || !Array.isArray(daySchedule)) return [];

    const duration = config.bookingRules.appointmentDuration || 30;
    const slots: string[] = [];

    daySchedule.forEach((period: any) => {
      if (!period.isAvailable) return;

      let current = period.startTime; // e.g., "09:00"
      const end = period.endTime; // e.g., "17:00"

      while (current < end) {
        const [hours, mins] = current.split(":").map(Number);
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayTime = `${displayHours}:${mins.toString().padStart(2, "0")} ${ampm}`;
        
        slots.push(displayTime);

        const totalMins = hours * 60 + mins + duration;
        const nextHours = Math.floor(totalMins / 60);
        const nextMins = totalMins % 60;
        current = `${nextHours.toString().padStart(2, "0")}:${nextMins.toString().padStart(2, "0")}`;
      }
    });

    return slots;
  }, [config, selectedDate]);

  const chunkedSlots = useMemo(() => {
    const rows = [];
    for (let i = 0; i < generatedSlots.length; i += 4) {
      rows.push(generatedSlots.slice(i, i + 4));
    }
    return rows;
  }, [generatedSlots]);

  if (generatedSlots.length === 0) {
    return <div className="text-center p-3 text-muted">No slots available for this date.</div>;
  }

  return (
    <>
      <div className="display-max">
        <div className="mt-4">
          {chunkedSlots.map((row, rowIndex) => (
            <div key={rowIndex} className="d-flex justify-content-start align-items-center gap-3 mb-3">
              {row.map((slot) => (
                <div key={slot} className="w-100">
                  <button
                    className={`btn btn-md ${selectedSlot === slot ? "btn-dark" : "btn-outline-dark"} border-radius-section text-nowrap w-100`}
                    onClick={() => onSlotSelect(slot)}
                  >
                    {slot}
                  </button>
                </div>
              ))}
              {/* Maintain layout grid even if row has fewer than 4 slots */}
              {row.length < 4 && Array.from({ length: 4 - row.length }).map((_, i) => (
                <div key={`empty-${i}`} className="w-100" />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="display-mini display-mob">
        <div className="mt-4 px-2">
          {chunkedSlots.map((row, rowIndex) => (
            <div key={rowIndex} className="d-flex justify-content-start align-items-center gap-1 mb-3">
              {row.map((slot) => (
                <div key={slot} className="w-100">
                  <button
                    className={`btn btn-sm ${selectedSlot === slot ? "btn-dark" : "btn-outline-dark"} border-radius-section text-nowrap w-100`}
                    onClick={() => onSlotSelect(slot)}
                  >
                    {slot}
                  </button>
                </div>
              ))}
              {row.length < 4 && Array.from({ length: 4 - row.length }).map((_, i) => (
                <div key={`empty-mob-${i}`} className="w-100" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default TimeSlots;
